
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import ActionButton from '@/components/ActionButton.vue';
import ReorderSettingTable from '@/components/ReorderSettingTable.vue';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';
import { SupplierList } from '@/collections/suppliers';
import { SupplierModel } from '@/models/supplier';
import { ReorderSettingParam } from '@/models/reorder_setting_param';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import ReorderParamForm from './ReorderParamForm.vue';
import GlobalParamForm from './GlobalParamForm.vue';
import { ReportModel } from '@/models/report';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import ReportFilter from '@/components/ReportFilter.vue';
import { AnalyticsTypeModel } from '@/models/analytics_type';
import { ParamModel } from '@/models/param';
import { ProductSegmentModel } from '@/models/product_segment';
import { MeasureList } from '@/collections/measures';
import { ReplenishmentSettingHelper } from '@/helpers/ReplenishmentSettingHelper';
import SpreadsheetSettings from '@/components/SpreadsheetSettings.vue';
import { ReplenishmentSettingHistory } from '@/models/replenishment_setting_history';
import ParameterHierarchyGuide from '@/components/ParameterHierarchyGuide.vue';
import SupplierOverviewTable from '@/components/SupplierOverviewTable.vue';
import { GlobalDefaults } from '@/models/supplier';
import { getParameterLabel } from '@/config/parameterLabels';

interface Option {
  sortBy?: any;
  sortDesc?: any;
  page?: string;
  itemsPerPage?: string;
  multiSort?: boolean;
}

interface FilterData {
  filterColumns: FilterColumnList;
  productSegment: ProductSegmentModel;
}

interface TableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
  filters?: FilterColumnList;
}

interface ScopeFilter {
  id: number | null;
  operator: 'eq' | 'ne' | 'all' | 'null';
  scope: 'supplier' | 'location';
}

@Component({
  components: {
    ActionButton,
    ReorderSettingTable,
    ReorderParamForm,
    GlobalParamForm,
    ReportFilter,
    SpreadsheetSettings,
    ParameterHierarchyGuide,
    SupplierOverviewTable,
  },
})
export default class ReorderParamSetting extends Vue {

  public get getReportFilter(): FilterColumnList {
    return this.filterData.filterColumns;
  }

  private get getSelectedSupplierName(): string {
    if (this.selectedSupplierId) {
      const supplier = this.suppliers.find((s) => s.id === this.selectedSupplierId);
      return supplier ? supplier.name : '';
    }
    return '';
  }

  private get getHierarchyLevel(): 'global' | 'supplier' | 'product' {
    return this.currentLevel;
  }

  private get getCurrentLevelTitle(): string {
    switch (this.currentLevel) {
      case 'global':
        return 'Global Settings';
      case 'supplier':
        return 'Supplier Settings';
      case 'product':
        return 'Product Settings';
      default:
        return 'Global Settings';
    }
  }

  private get suppliersWithParams() {
    // Add parameter status and counts to suppliers
    return this.suppliers.map((supplier) => ({
      ...supplier,
      parametersStatus: this.getSupplierParameterStatus(supplier),
      productCount: this.getSupplierProductCount(supplier),
    }));
  }

  private get isGlobalSetting(): boolean {
    return this.selectedProducts.length === 0;
  }

  // Computed properties for standardized labels
  private get leadTimeShortLabel(): string {
    return getParameterLabel('LEAD_TIME', 'short');
  }

  private get orderCycleShortLabel(): string {
    return getParameterLabel('ORDER_CYCLE', 'short');
  }

  private get safetyStockDaysShortLabel(): string {
    return getParameterLabel('SAFETY_STOCK_DAYS', 'short');
  }

  private get minStockQtyShortLabel(): string {
    return getParameterLabel('MIN_STOCK_QUANTITY', 'short');
  }

  private get maxStockQtyShortLabel(): string {
    return getParameterLabel('MAX_STOCK_QUANTITY', 'short');
  }

  private get minStockDaysShortLabel(): string {
    return getParameterLabel('MIN_STOCK_DAYS', 'short');
  }

  private get maxStockDaysShortLabel(): string {
    return getParameterLabel('MAX_STOCK_DAYS', 'short');
  }

  private get getGlobalDefaults(): GlobalDefaults {
    return {
      leadtime: this.currentParams.leadtime,
      orderCycle: this.currentParams.orderCycle,
      minStockCoverDays: this.currentParams.minStockCoverDays
    };
  }

  // Check if any time parameters have values to show in confirmation dialog
  private get hasTimeParametersToShow(): boolean {
    const hasLeadtime = this.hasParamValue(this.paramToConfirm?.leadtime);
    const hasOrderCycle = this.hasParamValue(this.paramToConfirm?.orderCycle);
    const hasMinStockCoverDays = this.hasParamValue(this.paramToConfirm?.minStockCoverDays);
    return hasLeadtime || hasOrderCycle || hasMinStockCoverDays;
  }

  // Helper to check if a param has a valid value (not null, undefined, or empty string)
  private hasParamValue(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  // Check if there are any parameters to show in confirmation dialog
  private get hasAnyParamsToShow(): boolean {
    if (!this.paramToConfirm) return false;

    // Time parameters (shown for all levels)
    if (this.hasTimeParametersToShow) return true;

    // For supplier/product levels, check additional params
    if (this.currentLevel === 'supplier' || this.currentLevel === 'product') {
      // Order quantities
      if (this.hasParamValue(this.paramToConfirm.fixOrderQuantity) ||
          this.hasParamValue(this.paramToConfirm.minOrderQuantity) ||
          this.hasParamValue(this.paramToConfirm.maxOrderQuantity)) return true;

      // Case quantity
      if (this.hasParamValue(this.paramToConfirm.caseQuantity)) return true;

      // Inventory quantities
      if (this.hasParamValue(this.paramToConfirm.minInventoryQuantity) ||
          this.hasParamValue(this.paramToConfirm.maxInventoryQuantity)) return true;

      // Stock cover days
      if (this.hasParamValue(this.paramToConfirm.maxStockCoverDays)) return true;

      // Replenishment exclusion
      if (this.paramToConfirm.replenishmentExclusion === true) return true;

      // Scope (product/variant)
      if (this.hasParamValue(this.paramToConfirm.scope)) return true;
    }

    return false;
  }

  @Prop({ required: true })
  public store!: StoreModel;
  @Prop({ type: [String, Number], default: 0 })
  public initialTab!: string | number;
  public loadingParams: boolean = false;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  public measure: MeasureModel = new MeasureModel();
  public dimension: DimensionModel = new DimensionModel();
  public analyticsType: AnalyticsTypeModel = new AnalyticsTypeModel();
  public readyToGetReport: boolean = false;
  public needRefreshData: boolean = false;
  public dates: string[] = [];
  public defaultRange: string = '30d';
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public hasSchemas!: boolean;

  private useSpreadsheets = false;
  private search = '';
  private selectedProducts: any[] = [];
  private saving = false;
  private suppliers: SupplierModel[] = [];
  private loadingSuppliers = false;
  private selectedSupplierId: number | null = null;

  // New properties
  private productScope: 'product' | 'variant' = 'variant';
  private stores: StoreLocationModel[] = [];
  private selectedStoreId: number | null = null;
  private loadingStores = false;

  // New properties for param dialog
  private paramDialogVisible = false;
  private currentParams = new ReorderSettingParam();
  private currentSupplier: SupplierModel | null = null;
  private currentlyEditingVariantId: string | null = null;

  private paramToConfirm = new ReorderSettingParam();

  // Supplier settings properties
  private supplierParams = new ReorderSettingParam();
  private supplierFormWarning: string = '';

  private reloadingProducts = false;
  private filterData: FilterData = {
    filterColumns: new FilterColumnList(),
    productSegment: new ProductSegmentModel(),
  };
  private initFilterColumns: FilterColumnList = new FilterColumnList();
  private filterReportColumns: any[] = [
  {
    name: 'Product Tags',
    code: 'product_tags',
    dataType: 'text',
    luisMapping: 'product_tags',
  },
  {
    name: 'Collections',
    code: 'product_collections',
    dataType: 'text',
    luisMapping: 'product_collections',
  },
  {
    name: 'Product',
    code: 'Item[Item Description]',
    dataType: 'text',
    luisMapping: 'by_product',
  },
  {
    name: 'SKU',
    code: 'product_sku',
    dataType: 'text',
    luisMapping: 'by_product_sku',
  },
  {
    name: 'Product Type',
    code: 'product_type',
    dataType: 'text',
    luisMapping: 'by_product_type',
  },
  {
    name: 'Vendor',
    code: 'Vendor Name',
    dataType: 'text',
    luisMapping: 'by_vendor',
  },
  {
    name: 'Variant',
    code: 'variant',
    dataType: 'text',
    luisMapping: 'variant',
    autoSelect: false,
    colorIndex: false,
    sortable: true,
  },
  {
    name: 'Barcode',
    code: 'product_barcode',
    dataType: 'text',
    luisMapping: 'product_barcode',
    autoSelect: false,
    colorIndex: false,
    sortable: true,
  },
  {
    name: 'Product ID',
    code: 'product_id',
    dataType: 'text',
    luisMapping: 'product_id',
  },
  {
    name: 'Variant ID',
    code: 'variant_id',
    dataType: 'text',
    luisMapping: 'variant_id',
  },
  {
    name: 'Supplier',
    code: 'supplier',
    dataType: 'text',
    luisMapping: 'supplier',
  },
  ];

  private confirmDialogVisible = false;
  private totalItems = 0;
  private isSelectAll = false;
  private showGuideDialog = false;
  private guideStep = 1;
  private showProcessingWarning = false;
  private currentLevel: 'global' | 'supplier' | 'product' = 'global';

  // New properties for bulk update
  private bulkUpdateDialogVisible = false;
  private bulkUpdateParams = new ReorderSettingParam();
  private bulkUpdateSelectedScope: 'product' | 'variant' = 'variant';
  private bulkUpdateFormWarning: string = '';
  private globalFormWarning: string = '';

  // Polling state management for auto-refresh
  private pollingInterval: number | null = null;
  private pollingAttempts: number = 0;
  private maxPollingAttempts: number = 60; // 5 minutes at 5s intervals
  private pollingIntervalMs: number = 5000; // 5 seconds
  private isPolling: boolean = false;

  // Force update state management
  private isForceUpdate: boolean = false;
  private forceUpdateConfirmed: boolean = false;

  // Selected supplier for updating
  private supplierToUpdate: SupplierModel | null = null;

  public async created() {
    // get the tab from the url
    const tab = this.$route.query.tab || '';
    if (tab && (tab === 'global' || tab === 'supplier' || tab === 'product')) {
      this.currentLevel = tab as 'global' | 'supplier' | 'product';
    } else {
      // Set currentLevel based on the prop before fetching data
      const tabValue = typeof this.initialTab === 'string' ? parseInt(this.initialTab, 10) : this.initialTab;
      if (!isNaN(tabValue) && (tabValue >= 0 && tabValue <= 2)) {
        this.currentLevel = tabValue === 0 ? 'global' : tabValue === 1 ? 'supplier' : 'product';
      }
    }

    await this.fetchGlobalParams();
    await this.fetchSuppliers();
    this.initReportFilterColumns();
  }

  public async mounted() {
    // Eagerly load product table data if not using spreadsheets initially
    if (!this.useSpreadsheets) {
      this.$nextTick(async () => {
        const table = this.$refs.productTable as any;
        if (table && typeof table.fetchProducts === 'function') {
          try {
            // We don't need to show the loading indicator here,
            // as the table has its own internal loading state.
            await table.fetchProducts();
          } catch (error) {
            // Error pre-loading product table data
            this.handleError('Failed to pre-load product data');
          }
        }
      });
    }
  }

  public beforeDestroy() {
    // Clean up polling interval to prevent memory leaks
    this.stopPollingForStatus();
  }

  public initReportFilterColumns() {
    // get the supplier id from the url
    const supplier = this.$route.query.supplier || '';
    if (supplier) {
      this.initFilterColumns = new FilterColumnList();
      this.initFilterColumns.add(new FilterColumnModel({
        name: 'Supplier',
        code: 'supplier',
        operator: 'eq',
        value: [supplier],
        dataType: 'text',
      }));
    }
  }

  public async initData() {
    const mesaureCode = 'replenishment_settings';
    this.measure.code = mesaureCode;
    if (this.measures.items.length === 0) {
        await this.measure.fetch();
    } else {
      for (const item of this.measures.items) {
        if (item.code === mesaureCode) {
          this.measure = item;
          break;
        }
      }
    }

    for (const item of this.measure.dimensions.items) {
      if (item.code === 'by_variant') {
        this.dimension = item;
        break;
      }
    }

    this.selectedProducts = [];
  }
  @Watch('initialTab')
  private onInitialTabChange(newValue: string | number) {
    const tabValue = typeof newValue === 'string' ? parseInt(newValue, 10) : newValue;
    if (!isNaN(tabValue) && (tabValue >= 0 && tabValue <= 2)) {
      this.currentLevel = tabValue === 0 ? 'global' : tabValue === 1 ? 'supplier' : 'product';
    }
  }
  @Watch('productScope')
  private onProductScopeChange(newValue: string) {
    // Reset selected products when switching scope
    this.selectedProducts = [];
    // Reset filter data
    this.filterData = {
      filterColumns: new FilterColumnList(),
      productSegment: new ProductSegmentModel(),
    };
  }
  @Watch('selectedSupplierId')
  private onSelectedSupplierIdChange(newValue: number) {
    if (newValue && this.currentLevel === 'global') {
      this.currentParams.supplierId = newValue;
      this.fetchGlobalParams();
    }
  }
  @Watch('currentLevel')
  private onCurrentLevelChange(newLevel: 'global' | 'supplier' | 'product') {
    // Stop polling if user navigates away from product settings
    if (newLevel !== 'product') {
      this.stopPollingForStatus();
    }

    // Check status when entering Product Settings (don't auto-start polling)
    if (newLevel === 'product' && !this.useSpreadsheets) {
      this.checkSettingSyncStatus();
    }
  }
  @Watch('selectedSupplierId')
  private async onSupplierChange(newSupplierId: number | null) {
    if (newSupplierId) {
      await this.fetchSupplierParams();
    } else {
      this.supplierParams = new ReorderSettingParam();
    }
  }

  private handleTotalItemsUpdate(totalItems: number) {
    this.totalItems = totalItems;
  }

  private async fetchGlobalParams() {
    this.loadingParams = true;
    try {
      this.currentParams = await ReplenishmentSettingHelper.getGlobalParams(
        {
          product: 'variant',
          location: '',
          supplier: this.selectedSupplierId ? 'specific' : 'global',
          parameter: 'global',
        },
        this.selectedSupplierId || 0,
      );
    } catch (error) {
      // Error fetching global params
      this.handleError('Failed to load global parameters');
    } finally {
      this.loadingParams = false;
    }
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const suppliers = new SupplierList();
      await suppliers.fetch();
      this.suppliers = suppliers.items;
    } catch (error) {
      // Error fetching suppliers
      this.handleError('Failed to load suppliers');
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private resetSupplierFilter() {
    this.selectedSupplierId = null;
  }

  private handleError(message: string) {
    EventBus.$emit('show-snackbar', message, 'error');
  }

  private getSupplierParameterStatus(supplier: SupplierModel): string {
    // Simple status determination - can be enhanced
    const hasParams = supplier.leadTime || supplier.reorderCycle;
    if (hasParams) {
      const paramCount = [supplier.leadTime, supplier.reorderCycle].filter((p) => p !== null && p !== undefined).length;
      return paramCount >= 2 ? 'complete' : 'partial';
    }
    return 'none';
  }

  private getSupplierProductCount(supplier: SupplierModel): number {
    // This would typically come from the API or be computed
    return supplier.productCount || 0;
  }

  private async applySetting(data: ReorderSettingParam | {
    params: ReorderSettingParam;
    forceUpdate?: boolean;
  }) {
    // Check form validity (for individual field rules)
    const form = this.$refs.paramForm as any;
    if (!form) {
      EventBus.$emit('show-snackbar', 'Form reference not found', 'error');
      return;
    }

    // Get the form element
    const vForm = form.$refs.form as any;
    if (!vForm) {
      EventBus.$emit('show-snackbar', 'Form element not found', 'error');
      return;
    }

    // Handle both old format (ReorderSettingParam) and new format ({ params, forceUpdate })
    let paramToUpdate: ReorderSettingParam;
    let forceUpdate = false;

    if ('params' in data && 'forceUpdate' in data) {
      // New format from GlobalParamForm
      paramToUpdate = data.params;
      forceUpdate = data.forceUpdate || false;
    } else {
      // Old format (direct ReorderSettingParam) - for backward compatibility
      paramToUpdate = data as ReorderSettingParam;
      forceUpdate = false;
    }

    // Set force update state for confirmation dialog
    this.isForceUpdate = forceUpdate;
    this.forceUpdateConfirmed = false;  // Reset confirmation

    this.paramToConfirm = new ReorderSettingParam();
    Object.assign(this.paramToConfirm, paramToUpdate);
    this.confirmDialogVisible = true;
  }

  private async confirmUpdate() {
    if (!this.paramToConfirm) { return; }

    // Additional check for force update confirmation
    if (this.isForceUpdate && !this.forceUpdateConfirmed) {
      EventBus.$emit('show-snackbar', 'Please confirm you understand this action', 'warning');
      return;
    }

    const filterColumns = new FilterColumnList();
    let settingScope = {
      product: '',
      supplier: '',
      parameter: '',
    };

    if (this.currentLevel === 'global') {
      // Global Settings
      settingScope = {
        product: 'variant',
        supplier: 'global',
        parameter: 'global',
      };
      this.paramToConfirm.scope = settingScope.product;
      this.paramToConfirm.supplierId = 0;

      // For global level, only include the 3 fields shown in GlobalParamForm
      // Clear other fields so they won't be sent in the payload
      this.paramToConfirm.fixOrderQuantity = null;
      this.paramToConfirm.minOrderQuantity = null;
      this.paramToConfirm.maxOrderQuantity = null;
      this.paramToConfirm.minInventoryQuantity = null;
      this.paramToConfirm.maxInventoryQuantity = null;
      this.paramToConfirm.maxStockCoverDays = null;
      this.paramToConfirm.caseQuantity = null;

    } else if (this.currentLevel === 'supplier') {
      // Supplier Settings
      settingScope = {
        product: 'variant',
        supplier: 'specific',
        parameter: 'supplier',
      };
      this.paramToConfirm.scope = settingScope.product;
      this.paramToConfirm.supplierId = this.selectedSupplierId || 0;

    } else {
      // Product Settings
      settingScope.parameter = 'specific';
      settingScope.product = this.paramToConfirm.scope;

      const supplierFilter = this.filterData.filterColumns.items.find((f) => f.code === 'supplier_id');
      if (supplierFilter?.operator === 'eq' && supplierFilter.value.length === 1) {
        settingScope.supplier = 'specific';
        this.paramToConfirm.supplierId = Number(supplierFilter.value[0]);
      } else {
        settingScope.supplier = 'global';
        this.paramToConfirm.supplierId = 0;
      }

      if (this.isSelectAll) {
        this.filterData.filterColumns.items.forEach((filter) => {
          filterColumns.add(filter);
        });
      } else {
        if (this.selectedProducts.length > 0) {
          const idField = settingScope.product === 'product' ? 'product_id' : 'variant_id';
          const selectionFilter = new FilterColumnModel();
          selectionFilter.code = idField;
          selectionFilter.operator = 'eq';
          selectionFilter.value = this.selectedProducts.map((product) => product[idField]);
          filterColumns.add(selectionFilter);
        } else {
          EventBus.$emit('show-snackbar', 'Please select at least one item or use filters', 'warning');
          this.saving = false;
          return;
        }
      }
    }

    this.saving = true;
    try {
      if (this.currentLevel === 'supplier') {
        // For supplier level, update the supplier directly
        // Backend will auto-update the reorder param settings
        if (!this.supplierToUpdate) {
          EventBus.$emit('show-snackbar', 'No supplier selected', 'error');
          return;
        }

        if (this.isForceUpdate) {
          // Force Update Mode: Only send checked fields to prevent overwriting variant values
          await this.supplierToUpdate.forceUpdateParams({
            leadTime: this.paramToConfirm.leadtime,
            reorderCycle: this.paramToConfirm.orderCycle,
            minStockCoverDays: this.paramToConfirm.minStockCoverDays,
            minOrderValue: this.paramToConfirm.minOrderValue,
            maxOrderValue: this.paramToConfirm.maxOrderValue,
            minOrderQuantity: this.paramToConfirm.minOrderQuantity,
            maxOrderQuantity: this.paramToConfirm.maxOrderQuantity,
            scope: this.paramToConfirm.scope,
          });
        } else {
          // Normal Mode: Update all params to supplier record
          // Only update fields that have explicit values (not null/undefined) to avoid overwriting existing data
          if (this.paramToConfirm.leadtime !== null && this.paramToConfirm.leadtime !== undefined) {
            this.supplierToUpdate.leadTime = this.paramToConfirm.leadtime;
          }
          if (this.paramToConfirm.orderCycle !== null && this.paramToConfirm.orderCycle !== undefined) {
            this.supplierToUpdate.reorderCycle = this.paramToConfirm.orderCycle;
          }
          if (this.paramToConfirm.minStockCoverDays !== null && this.paramToConfirm.minStockCoverDays !== undefined) {
            this.supplierToUpdate.minStockCoverDays = this.paramToConfirm.minStockCoverDays;
          }
          if (this.paramToConfirm.minOrderValue !== null && this.paramToConfirm.minOrderValue !== undefined) {
            this.supplierToUpdate.minOrderValue = this.paramToConfirm.minOrderValue;
          }
          if (this.paramToConfirm.maxOrderValue !== null && this.paramToConfirm.maxOrderValue !== undefined) {
            this.supplierToUpdate.maxOrderValue = this.paramToConfirm.maxOrderValue;
          }
          if (this.paramToConfirm.minOrderQuantity !== null && this.paramToConfirm.minOrderQuantity !== undefined) {
            this.supplierToUpdate.minOrderQuantity = this.paramToConfirm.minOrderQuantity;
          }
          if (this.paramToConfirm.maxOrderQuantity !== null && this.paramToConfirm.maxOrderQuantity !== undefined) {
            this.supplierToUpdate.maxOrderQuantity = this.paramToConfirm.maxOrderQuantity;
          }
          if (this.paramToConfirm.scope) {
            this.supplierToUpdate.scope = this.paramToConfirm.scope;
          }

          await this.supplierToUpdate.update({ forceUpdate: false });
        }

        // Reload suppliers list to refresh the table with updated parameters
        await this.fetchSuppliers();

        // Close dialog after data is refreshed
        this.confirmDialogVisible = false;
        this.supplierToUpdate = null;

        EventBus.$emit('show-snackbar',
          this.isForceUpdate
            ? 'Force update completed: All supplier variants updated'
            : 'Supplier parameters updated successfully',
          'success'
        );
      } else {
        // For global and product levels, use the existing saveReorder flow
        // Simplified setting object - only includes fields used by backend
        const setting = {
          forceUpdate: this.isForceUpdate,
          scope: settingScope.parameter  // "global" | "supplier" | "specific" - for logging only
        };

        await this.paramToConfirm.saveReorder(filterColumns, setting, settingScope as any);
        this.selectedProducts = [];
        this.isSelectAll = false;

        if (this.currentLevel === 'product') {
          // Close dialog before starting polling
          this.confirmDialogVisible = false;
          // Start auto-polling to check when processing completes
          this.startPollingForStatus();
        } else {
          // For global level - close dialog after save
          this.confirmDialogVisible = false;
          EventBus.$emit('show-snackbar',
            this.isForceUpdate
              ? 'Force update completed: All product variants updated'
              : 'Parameters updated successfully',
            'success'
          );
        }
      }
    } catch (error: any) {
      // Error saving
      const errorMessage = error?.message || 'Failed to save parameters';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.saving = false;
      this.isForceUpdate = false;  // Reset force update flag
      this.forceUpdateConfirmed = false;  // Reset confirmation
    }
  }

  private customFilter(item: string, queryText: string, itemText: string): boolean {
    const text = itemText.toLowerCase();
    const searchText = queryText.toLowerCase();
    return text.indexOf(searchText) > -1;
  }

  private createParamsFromItem(item: any): ReorderSettingParam {
    const params = new ReorderSettingParam();

    // Set parameters from item data
    params.leadtime = item['[vendor_lead_time]'];
    params.orderCycle = item['[reorder_cycle]'];
    params.fixOrderQuantity = item['[fixed_order_quantity]'];
    params.minOrderQuantity = item['[min_order_quantity]'];
    params.maxOrderQuantity = item['[max_order_quantity]'];
    params.replenishmentExclusion = item['[replenishment_exclusion]'] === '1';

    if (this.selectedSupplierId) {
      params.supplierId = this.selectedSupplierId;
    }

    return params;
  }

  private async reloadProducts() {
    this.reloadingProducts = true;
    // this.showProcessingWarning = false;
    try {
      const history = new ReplenishmentSettingHistory();
      const lastSettingStatus = await history.getLastSettingStatus();

      if (lastSettingStatus === 'DONE') {
        this.showProcessingWarning = false;
        if (this.$refs.productTable) {
          const table = this.$refs.productTable as any;
          table.fetchProducts();
        }
      } else if (['PROCESSING', 'PENDING', 'RUNNING'].includes(lastSettingStatus)) {
        this.showProcessingWarning = true;
        // Replenishment settings still processing. Table refresh skipped.
      }
    } catch (error) {
      // Error during product reload or status check
      this.handleError('Failed to check status or reload products');
    } finally {
      this.reloadingProducts = false;
    }
  }

  private async checkSettingSyncStatus() {
    this.reloadingProducts = true;
    try {
      const history = new ReplenishmentSettingHistory();
      const lastSettingStatus = await history.getLastSettingStatus();

      if (lastSettingStatus === 'DONE' || lastSettingStatus === 'COMPLETED') {
        this.showProcessingWarning = false;
        // Auto-load products when status is processed
        if (this.$refs.productTable) {
          const table = this.$refs.productTable as any;
          if (typeof table.fetchProducts === 'function') {
            await table.fetchProducts();
          }
        }
      } else if (['PROCESSING', 'PENDING', 'RUNNING'].includes(lastSettingStatus)) {
        this.showProcessingWarning = true;
        // Start polling to auto-refresh when processing completes
        this.startPollingForStatus();
      }
    } catch (error) {
      this.handleError('Failed to check status or reload products');
    } finally {
      this.reloadingProducts = false;
    }
  }

  private selectAll() {
    this.isSelectAll = true;
    if (this.$refs.productTable) {
      const table = this.$refs.productTable as any;
      if (table.products && table.products.length > 0) {
        this.selectedProducts = [...table.products];
      }
    }
  }

  private clearSelection() {
    this.isSelectAll = false;
    this.selectedProducts = [];
  }

  private openBulkUpdateDialog() {
    this.bulkUpdateParams = new ReorderSettingParam();
    this.bulkUpdateSelectedScope = this.productScope;
    this.bulkUpdateFormWarning = '';
    this.bulkUpdateDialogVisible = true;
    this.$nextTick(() => {
      const form = this.$refs.bulkParamForm as any;
      if (form && form.$refs.form) {
        form.$refs.form.resetValidation();
      }
    });
  }

  private closeBulkUpdateDialog() {
    this.bulkUpdateDialogVisible = false;
  }

  private async applyBulkUpdate() {
    if (this.selectedProducts.length === 0 && !this.isSelectAll) {
      EventBus.$emit('show-snackbar', 'Please select items or use \'Select All\' before bulk updating.', 'warning');
      return;
    }

    const formComponent = this.$refs.bulkParamForm as any;
    if (!formComponent) {
      EventBus.$emit('show-snackbar', 'Bulk update form reference not found', 'error');
      return;
    }
    const vForm = formComponent.$refs.form as any;
    if (!vForm) {
      return;
    }
    const isValid = await vForm.validate();
    if (!isValid) {
      return;
    }

    const paramsFromBulkForm = formComponent.reorderSettingParam as ReorderSettingParam;
    const hasValue =
      (paramsFromBulkForm.leadtime !== undefined && paramsFromBulkForm.leadtime !== null) ||
      (paramsFromBulkForm.orderCycle !== undefined && paramsFromBulkForm.orderCycle !== null) ||
      (paramsFromBulkForm.fixOrderQuantity !== undefined && paramsFromBulkForm.fixOrderQuantity !== null) ||
      (paramsFromBulkForm.minOrderQuantity !== undefined && paramsFromBulkForm.minOrderQuantity !== null) ||
      (paramsFromBulkForm.maxOrderQuantity !== undefined && paramsFromBulkForm.maxOrderQuantity !== null) ||
      (paramsFromBulkForm.caseQuantity !== undefined && paramsFromBulkForm.caseQuantity !== null) ||
      paramsFromBulkForm.replenishmentExclusion === true ||
      (paramsFromBulkForm.minInventoryQuantity !== undefined && paramsFromBulkForm.minInventoryQuantity !== null) ||
      (paramsFromBulkForm.maxInventoryQuantity !== undefined && paramsFromBulkForm.maxInventoryQuantity !== null) ||
      (paramsFromBulkForm.minStockCoverDays !== undefined && paramsFromBulkForm.minStockCoverDays !== null) ||
      (paramsFromBulkForm.maxStockCoverDays !== undefined && paramsFromBulkForm.maxStockCoverDays !== null);

    if (!hasValue) {
      this.bulkUpdateFormWarning = 'Please set at least one parameter in the bulk update form.';
      return;
    }

    this.bulkUpdateFormWarning = '';

    this.paramToConfirm = new ReorderSettingParam();
    Object.assign(this.paramToConfirm, paramsFromBulkForm);
    this.paramToConfirm.scope = this.bulkUpdateSelectedScope;

    this.closeBulkUpdateDialog();
    this.confirmDialogVisible = true;
  }

  // Handle hierarchy level clicks
  private handleHierarchyLevelClick(level: 'global' | 'supplier' | 'product') {
    this.currentLevel = level;
  }

  // Supplier settings methods
  private async fetchSupplierParams() {
    if (!this.selectedSupplierId) { return; }

    try {
      this.supplierParams = await ReplenishmentSettingHelper.getGlobalParams(
        {
          product: 'variant',
          location: '',
          supplier: 'specific',
          parameter: 'supplier',
        },
        this.selectedSupplierId,
      );
    } catch (error) {
      // Error fetching supplier params
      this.handleError('Failed to load supplier parameters');
    }
  }

  private async applySupplierSetting(paramToUpdate: ReorderSettingParam) {
    if (!this.selectedSupplierId) { return; }

    // Set supplier-specific parameters
    paramToUpdate.supplierId = this.selectedSupplierId;
    paramToUpdate.scope = 'variant';

    this.paramToConfirm = new ReorderSettingParam();
    Object.assign(this.paramToConfirm, paramToUpdate);
    this.confirmDialogVisible = true;
  }

  // Handler for supplier table events
  private handleSupplierUpdated(supplier: SupplierModel) {
    // Find and update the supplier in our local array
    const index = this.suppliers.findIndex((s) => s.id === supplier.id);
    if (index !== -1) {
      this.suppliers.splice(index, 1, supplier);
    }
  }

  private openBulkSupplierUpdate() {
    // This could open a dialog for bulk updating multiple suppliers
    EventBus.$emit('show-snackbar', 'Bulk supplier update coming soon!', 'info');
  }

  private handleSupplierForceUpdate(data: { supplier: SupplierModel; params: ReorderSettingParam; forceUpdate: boolean }) {
    // Set the selected supplier for context
    this.selectedSupplierId = data.supplier.id;
    // Store the supplier object for updating
    this.supplierToUpdate = new SupplierModel(data.supplier);

    // Set the parameters to confirm
    this.paramToConfirm = new ReorderSettingParam();
    Object.assign(this.paramToConfirm, data.params);

    // Set force update state
    this.isForceUpdate = data.forceUpdate;
    this.forceUpdateConfirmed = false;

    // Show confirmation dialog (transforms current view into confirmation)
    this.confirmDialogVisible = true;
  }

  /**
   * Start polling for setting status after save
   * Polls every 5 seconds, max 5 minutes
   * Automatically stops when DONE or on timeout
   */
  private startPollingForStatus(): void {
    // Prevent multiple polling instances
    if (this.isPolling) {
      this.stopPollingForStatus();
    }

    this.isPolling = true;
    this.pollingAttempts = 0;
    this.showProcessingWarning = true;

    // Initial immediate check
    this.checkStatusAndRefresh();

    // Start interval polling
    this.pollingInterval = window.setInterval(async () => {
      this.pollingAttempts++;

      // Timeout after max attempts (5 minutes)
      if (this.pollingAttempts >= this.maxPollingAttempts) {
        this.stopPollingForStatus();
        this.showProcessingWarning = false;
        EventBus.$emit('show-snackbar',
          'Processing is taking longer than expected. Please refresh manually or check back later.',
          'warning',
          8000
        );
        return;
      }

      await this.checkStatusAndRefresh();
    }, this.pollingIntervalMs);
  }

  private isStatusCheckInProgress: boolean = false;

  /**
   * Check current processing status and refresh table if done
   */
  private async checkStatusAndRefresh(): Promise<void> {
    if (this.isStatusCheckInProgress) {
      return;
    }
    this.isStatusCheckInProgress = true;
    try {
      const history = new ReplenishmentSettingHistory();
      const lastSettingStatus = await history.getLastSettingStatus();

      if (lastSettingStatus === 'DONE' || lastSettingStatus === 'COMPLETED') {
        // ✅ Success! Stop polling and refresh table
        this.stopPollingForStatus();
        this.showProcessingWarning = false;

        // Refresh the product table with updated parameters
        if (this.$refs.productTable) {
          const table = this.$refs.productTable as any;
          await table.fetchProducts();
        }

        // Show success notification
        EventBus.$emit('show-snackbar',
          'Reorder parameters have been applied successfully!',
          'success',
          5000
        );

      } else if (['PROCESSING', 'PENDING', 'RUNNING'].includes(lastSettingStatus)) {
        // Still processing, continue polling
        this.showProcessingWarning = true;

      } else if (lastSettingStatus === 'FAILED' || lastSettingStatus === 'ERROR') {
        // ❌ Error state detected
        this.stopPollingForStatus();
        this.showProcessingWarning = false;
        EventBus.$emit('show-snackbar',
          'Failed to apply reorder parameters. Please try again or contact support.',
          'error',
          8000
        );
      } else if (lastSettingStatus) {
        // Unknown status - log warning but don't stop polling yet
        console.warn('Unknown replenishment setting status:', lastSettingStatus);
      }

    } catch (error) {
      console.error('Error polling status:', error);
    } finally {
      this.isStatusCheckInProgress = false;
    }
  }

  /**
   * Stop polling and cleanup interval
   */
  private stopPollingForStatus(): void {
    if (this.pollingInterval !== null) {
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    this.pollingAttempts = 0;
  }
}
